import { Stack, Typography } from '@mui/material';
import { ProductInformation, ProductInformationCard } from '@retail/products/components';
import { MgItemCard, MgSupplierItemCard } from '@retail/products/types';
import { useCallback, useMemo } from 'react';
import { MgItemGWP } from './Environment';
import { UnderlinedTabs } from '@shared/components';
import { JSONContainer } from './JSONContainer';
import { useDisclosure, useSearchParamState } from '@shared/hooks';
import { SuspenseWithSentryErrorBoundary } from '@retail/components';
import { useProductsTFunction } from '@retail/products/i18n';

import { alphabeticSort } from '@shared/utils';
import { supplierItemSearchParam } from '@retail/utils';

interface Props {
  mgItem: MgItemCard;
  canReadExtendedItemInfo: boolean;
}

export function ProductInformationContainer({ mgItem, canReadExtendedItemInfo }: Props) {
  const t = useProductsTFunction();
  const { isOpen, onToggle } = useDisclosure();
  const { searchParams, handleFilterChange } = useSearchParamState();

  const searchParamSupplierItem = useMemo(
    () =>
      mgItem.mgSupplierItems.find(
        (supplierItem) =>
          `${supplierItem.mgSupplierItemId}` === searchParams.get(supplierItemSearchParam)
      ),
    [mgItem.mgSupplierItems, searchParams]
  );

  const selectedSupplierItem = useMemo(
    () => searchParamSupplierItem ?? mgItem.getFirstSupplierItem(),
    [mgItem, searchParamSupplierItem]
  );
  const setSelectedSupplierItem = useCallback(
    (supplierItem: MgSupplierItemCard) =>
      handleFilterChange({ filterType: supplierItemSearchParam, value: supplierItem.mgSupplierItemId }),
    [handleFilterChange]
  );

  const selectSupplierItemTab = (_: any, newTab: number) =>
    setSelectedSupplierItem(
      mgItem.mgSupplierItems.find((item) => item.mgSupplier.mgSupplierId === newTab) ??
        mgItem.getFirstSupplierItem()
    );

  const supplierItemTabs = useMemo(
    () =>
      mgItem.mgSupplierItems.sort(alphabeticSort('supplierItemId')).map((item) => {
        return {
          label: (
            <Stack alignItems="flex-start">
              <Typography variant="subtitle1" lineHeight={1}>
                {item.mgSupplier.name}
              </Typography>
              <Typography variant="body2">
                {t(`products.mgItem.details.externalId.${item.externalSource}`)} {item.externalId}
              </Typography>
            </Stack>
          ),
          value: item.mgSupplier.mgSupplierId,
        };
      }),
    [mgItem.mgSupplierItems, t]
  );

  return (
    <Stack p={2} gap={1}>
      <UnderlinedTabs
        id="supplierItemTabs"
        tabs={supplierItemTabs}
        currentTab={selectedSupplierItem?.mgSupplier.mgSupplierId}
        onChange={selectSupplierItemTab}
      />
      <ProductInformation selectedSupplierItem={selectedSupplierItem} />
      <MgItemGWP mgItem={mgItem} selectedSupplierItem={selectedSupplierItem} />
      {canReadExtendedItemInfo && (
        <ProductInformationCard title="JSON" expandable isExpanded={isOpen} onExpand={onToggle}>
          {isOpen ? (
            <SuspenseWithSentryErrorBoundary>
              <JSONContainer
                selectedMgSupplierId={selectedSupplierItem.mgSupplier.mgSupplierId}
                mgItem={mgItem}
              />
            </SuspenseWithSentryErrorBoundary>
          ) : (
            <Stack />
          )}
        </ProductInformationCard>
      )}
    </Stack>
  );
}
